import React from "react";
import CaseStudy from "../case/CaseStudy";
import {
  Section,
  Figure,
  Sticky,
  Stage,
  Specs,
  Beat,
  Grid,
} from "../case/blocks";
import { useStageParallax, PanGallery } from "../case/motion";
import useReveal from "../../../lib/useReveal";
import "../case/case.scss";
import "./atmos.scss";

import AtmosMain from "../../../images/Atmos-main.png";
import AtmosIssueHands from "../../../images/atmos-issue-5.png";
import AtmosIssueTools from "../../../images/atmos-issue-4.png";
import AtmosIssueWasher from "../../../images/atmos-issue-3.png";
import AtmosIconClock from "../../../images/atmos-issue-1.png";
import AtmosIconKit from "../../../images/atmos-issue-2.png";
import AtmosIconTap from "../../../images/atmos-issue-6.png";
import FlagSpain from "../../../images/flag-spain.png";
import FlagUSA from "../../../images/flag-usa.png";
import LogoKawasaki from "../../../images/logo-kawasaki.png";
import LogoBMW from "../../../images/logo-bmw.png";
import LogoYamaha from "../../../images/logo-yamaha.png";
import AtmosToolConn1 from "../../../images/atmos-tool-conn-1.png";
import AtmosToolConn2 from "../../../images/atmos-tool-conn-2.png";
import AtmosDeviceV2 from "../../../images/atmos-device-v2.png";
import AtmosSequence from "../../../images/atmos-sequence.webp";
import AtmosStoryboard from "../../../images/atmos-storyboard.png";
import AtmosConceptA from "../../../images/atmos-concept-a.png";
import AtmosConceptB from "../../../images/atmos-concept-b.png";
import AtmosConceptC from "../../../images/atmos-concept-c.png";
import AtmosRef1 from "../../../images/atmos-ref-1.png";
import AtmosRef2 from "../../../images/atmos-ref-2.png";
import AtmosBaseExploded from "../../../images/atmos-base-exploded.png";
import AtmosInUse from "../../../images/atmos-inuse-bg.png";
import AtmosRenderDetail from "../../../images/atmos-render-detail.png";
import AtmosSteamBg from "../../../images/atmos-steam-bg.jpg";
import LogoKeyshot from "../../../images/logo-keyshot.png";
import LogoAfterEffects from "../../../images/logo-aftereffects.png";
import LogoPremiere from "../../../images/logo-premiere.png";
import LogoRhino from "../../../images/logo-rhino.png";
import LogoSolidworks from "../../../images/logo-solidworks.png";
import AtmosCloth1 from "../../../images/atmos-cloth-1.png";
import AtmosCloth2 from "../../../images/atmos-cloth-2.png";
import AtmosCloth3 from "../../../images/atmos-cloth-3.png";
import AtmosCloth4 from "../../../images/atmos-cloth-4.png";
import AtmosWorkshop from "../../../images/atmos-workshop.png";
import AtmosConceptSketch from "../../../images/atmos-concept-sketch.png";

const ISSUES = [
  {
    img: AtmosIssueHands,
    alt: "Pair of hands stained with grime from cleaning",
    text: "Cleaning a motorbike by hand can be complicated and time consuming.",
    note: "It takes on average of 40 minutes to clean a motorbike.",
  },
  {
    img: AtmosIssueTools,
    alt: "Rider surrounded by a pressure washer, sponge and brushes",
    text: "The amount of tools and soaps required to clean can be difficult to manage for each wash.",
  },
  {
    img: AtmosIssueWasher,
    alt: "Pressure washer spraying into a motorbike's engine and electrics",
    text: "Standard pressure washers pose a risk to damaging the sensitive components of the bike.",
  },
];

const SOFTWARE = [
  {
    use: "Rendering & visualisation",
    logos: [{ src: LogoKeyshot, name: "KeyShot" }],
  },
  {
    use: "Animation, VFX & video editing",
    logos: [
      { src: LogoAfterEffects, name: "After Effects" },
      { src: LogoPremiere, name: "Premiere Pro" },
    ],
  },
  {
    use: "3D modelling",
    logos: [
      { src: LogoRhino, name: "Rhino" },
      { src: LogoSolidworks, name: "SolidWorks", wide: true },
    ],
  },
];

// Brand marks are the bikes each interviewee rides; flags are where they live.
const VOICES = [
  {
    quote:
      "I like to clean my bike regularly and every time I do it, it takes me much longer because I use so many tools.",
    bike: LogoKawasaki,
    bikeName: "Kawasaki",
    flag: FlagSpain,
    place: "Spain",
  },
  {
    quote:
      "Where I live I have restrictions on water usage and certain chemical products.",
    bike: LogoBMW,
    bikeName: "BMW",
    flag: FlagUSA,
    place: "United States",
  },
  {
    quote:
      "Every time I clean my bike it is impossible not to get grease on my hands.",
    bike: LogoYamaha,
    bikeName: "Yamaha",
    flag: FlagUSA,
    place: "United States",
  },
];

const CONCEPTS = [
  { img: AtmosConceptA, label: "Robust" },
  { img: AtmosConceptB, label: "Minimal" },
  { img: AtmosConceptC, label: "Dynamic" },
];

const ATMOS = () => {
  useStageParallax();
  const briefRef = useReveal({ threshold: 0.08 });

  return (
    <CaseStudy
      slug="/atmos"
      title="ATMOS"
      tagline="Student Master's Project"
      year="2024"
      client={{ name: "Poli.Design" }}
      roles={[
        "Conceptualisation & sketching",
        "User interviews & ergonomics",
        "3D modeling & visualisation",
      ]}
      hero={{ src: AtmosMain, alt: "ATMOS motorbike cleaning device render" }}
      lead={
        <p>
          Atmos is a multifunctional motorbike cleaning device made to help the
          user clean their motorbike at home, preventing damage to electrical
          components and other sensitive areas.
        </p>
      }
    >
      <Section
        id="issue"
        title="What's the issue?"
        lead="Cleaning a motorbike by hand can be complicated and time consuming."
      >
        <Grid min={280}>
          {ISSUES.map((i) => (
            <div className="atmos-issue" key={i.text}>
              <img src={i.img} alt={i.alt} loading="lazy" decoding="async" />
              <p>{i.text}</p>
              {i.note && <p className="atmos-issue__note">{i.note}</p>}
            </div>
          ))}
        </Grid>
      </Section>

      <Section id="voices">
        <header className="atmos-voices-head" data-reveal>
          <h2 className="cs-h2" id="voices-h">
            What do bikers say?
          </h2>
          <ul className="atmos-voices-icons">
            <li>
              <img src={AtmosIconTap} alt="Tap crossed out for water restrictions" />
            </li>
            <li>
              <img src={AtmosIconClock} alt="Clock" />
            </li>
            <li>
              <img src={AtmosIconKit} alt="Pressure washer, mitt and brushes" />
            </li>
          </ul>
        </header>
        <Grid min={300}>
          {VOICES.map((v) => (
            <figure className="atmos-voice" key={v.quote}>
              <blockquote>{v.quote}</blockquote>
              <figcaption>
                <span className="u-mono">User background</span>
                <span className="atmos-voice__marks">
                  <img src={v.bike} alt={v.bikeName} />
                  <img src={v.flag} alt={v.place} className="atmos-voice__flag" />
                </span>
              </figcaption>
            </figure>
          ))}
        </Grid>
      </Section>

      {/* One band over the steam photo, in place of the separate statement,
          brief and experience-area sections. */}
      <section
        className="atmos-brief"
        id="brief"
        ref={briefRef}
        aria-labelledby="brief-h"
        style={{ "--brief-bg": `url(${AtmosSteamBg})` }}
      >
        <div className="atmos-brief__inner">
          <div className="atmos-brief__cards">
            <div className="atmos-card" data-reveal>
              <h2 className="atmos-brief__title" id="brief-h">
                Project
                <br />
                Brief
              </h2>
              <blockquote className="atmos-brief__quote">
                Design a product that enables the user to clean their motorbike
                at home, preventing damage to electrical components and other
                sensitive areas.
              </blockquote>
              <p className="atmos-brief__label">User Group</p>
              <p className="atmos-brief__muted">Beginner and Veteran Bikers</p>
            </div>

            <div className="atmos-card" data-reveal>
              <h3 className="atmos-card__heading">Softwares used</h3>
              <ul className="atmos-tools">
                {SOFTWARE.map((group) => (
                  <li key={group.use}>
                    <span className="atmos-tools__logos">
                      {group.logos.map((logo) => (
                        <img
                          key={logo.name}
                          src={logo.src}
                          alt={logo.name}
                          className={logo.wide ? "is-wide" : undefined}
                        />
                      ))}
                    </span>
                    <span className="atmos-tools__use">{group.use}</span>
                  </li>
                ))}
              </ul>

              <h3 className="atmos-card__heading">My role</h3>
              <ul className="atmos-roles">
                <li>Conceptualisation &amp; sketching</li>
                <li>User interviews &amp; ergonomics</li>
                <li>3D modeling &amp; visualisation</li>
              </ul>
            </div>
          </div>

          <div className="atmos-brief__side">
            <div data-reveal>
              <h3 className="atmos-brief__subtitle">
                New User
                <br />
                Experience Areas
              </h3>
              <ul className="atmos-areas">
                <li>
                  <img src={AtmosToolConn1} alt="" />
                  <span>Connection of the tools</span>
                </li>
                <li>
                  <img src={AtmosToolConn2} alt="" />
                  <span>Storage of the tools</span>
                </li>
              </ul>
            </div>

            <div data-reveal>
              <h3 className="atmos-brief__subtitle">
                Technology selected
                <br />
                Steam cleaning
              </h3>
              <ul className="atmos-points">
                <li>
                  The low water content of the steam helps to{" "}
                  <strong>prevent damage</strong> to sensitive electronics on
                  the bike.
                </li>
                <li>
                  The dry steam helps to <strong>quickly dry</strong>.
                </li>
                <li>
                  <strong>Easily removes dirt</strong> in hard-to-reach areas.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Stage
        src={AtmosRenderDetail}
        alt="Detail render of the ATMOS device body"
        title="Steam, not pressure."
        height="100dvh"
      />

      <Sticky
        media={<img src={AtmosDeviceV2} alt="ATMOS device with dimensions" />}
      >
        <Beat title="General Dimensions">
          <p>
            The body was sized around a one-handed grip and a nozzle that can
            reach into narrow areas of the bike.
          </p>
        </Beat>
        <Specs
          items={[
            { label: "190 mm", body: "Overall body height" },
            { label: "50 mm", body: "Nozzle reach for metal and narrow areas" },
          ]}
          columns={1}
        />
      </Sticky>

      <Section id="storyboard" title="How do I use it?">
        <Figure
          src={AtmosStoryboard}
          alt="Storyboard showing the steps of cleaning a motorbike with ATMOS"
          bleed
          frame={false}
        />
      </Section>

      <Section id="sketches" title="Sketching" />

      <PanGallery label="ATMOS concept sketches">
        <img src={AtmosConceptSketch} alt="Concept sketches of the ATMOS body" />
        <img src={AtmosWorkshop} alt="Workshop mock-ups of the device" />
        <img src={AtmosCloth1} alt="Cleaning cloth attachment study one" />
        <img src={AtmosCloth2} alt="Cleaning cloth attachment study two" />
        <img src={AtmosCloth3} alt="Cleaning cloth attachment study three" />
        <img src={AtmosCloth4} alt="Cleaning cloth attachment study four" />
      </PanGallery>

      <Section id="ideation" title="The Ideation Journey">
        <div className="atmos-concepts">
          {CONCEPTS.map((c) => (
            <figure key={c.label}>
              <img src={c.img} alt={`${c.label} concept direction`} loading="lazy" />
              <figcaption className="u-mono">{c.label}</figcaption>
            </figure>
          ))}
        </div>
      </Section>

      <Section
        id="final-concept"
        title="Final Concept"
        lead="The ejector mechanism took its reference from a hand blender."
      >
        <Grid min={300}>
          <Figure src={AtmosRef1} alt="Hand blender ejector mechanism reference" />
          <Figure
            src={AtmosRef2}
            alt="Second mechanism reference study"
            caption="Hand blender ejector mechanism"
          />
        </Grid>
      </Section>

      <Section id="exploded" title="Exploded view">
        <Figure
          src={AtmosBaseExploded}
          alt="Exploded view of the ATMOS base and internal components"
          bleed
          frame={false}
        />
      </Section>

      <Section id="motion" title="In motion">
        <Figure
          src={AtmosSequence}
          alt="Animated turntable of the ATMOS device"
          max="520px"
        />
      </Section>

      <Stage
        src={AtmosInUse}
        alt="ATMOS in use cleaning a motorbike"
        title="Cleaning at home, without the risk."
        height="100dvh"
      />
    </CaseStudy>
  );
};

export default ATMOS;
